import { SearchParams } from '#types/generated/graphql-types';

// Type `SearchFiltersForVMS` is used to represent the query parameters in the URL
export type SearchFiltersForVMS = {
  categorySlugs?: string[];
  years?: string[];
  [key: string]: string[] | string | undefined;
};

// List of keys explicitly defined in GraphQL Input 'SearchParams'
const STATIC_PARAMS = new Set([
  'categoryIds',
  'categorySlugs',
  'contentType',
  'itemsPerPage',
  'page',
  'needle',
  'sort',
  'tags',
  'dateFrom',
  'dateTo',
  'years',
]);

/**
 * Formats a flat dictionary of search query parameters into a strictly typed GraphQL `SearchParams` object.
 *
 *   input SearchParams {
 *     categoryIds: [String]
 *     categorySlugs: [String]
 *     contentType: ContentType
 *     attributes: [SearchAttributeInput!]
 *     itemsPerPage: Int
 *     needle: String
 *     page: Int
 *     sort: Sort
 *     tags: [String!]
 *     dateFrom: String
 *     dateTo: String
 *     years: [Int]
 *   }
 *
 * This function is necessary because our frontend code (URL hooks, React state) treats
 * all filters as a flat object, but the GraphQL API requires a separation between:
 *   1. "Static" fields explicitly defined in the schema (e.g., `needle`, `page`).
 *   2. "Dynamic" fields which must be passed via the generic `attributes` array.
 *
 * @param filters - A flat object containing both standard params and dynamic facet filters.
 * @returns A strictly typed SearchParams object ready for the Apollo query variable.
 *
 * @example
 * Standard Fields Only -> Passed through as-is
 *   formatSearchParams({
 *     needle: "test",
 *     page: 1,
 *     categorySlugs: ["news"]
 *     md_meme_phrases: "67",
 *     md_actor: ["john-doe"],
 *   })
 * Returns:
 *   {
 *     needle: "test",
 *     page: 1,
 *     categorySlugs: ["news"]
 *     attributes: [
 *       { key: "md_meme_phrases", values: ["67"] }
 *       { key: "md_actor", values: ["john-doe"] }
 *     ]
 *   }
 */
export const formatSearchParams = (
  filters: Record<string, string | string[] | number | number[] | undefined>
): SearchParams => {
  const standardParams: Partial<SearchParams> = {};
  const attributes: { key: string; values: string[] }[] = [];

  Object.keys(filters).forEach((key) => {
    const value = filters[key];

    // Clean up empty/undefined values
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;

    if (STATIC_PARAMS.has(key)) {
      if (key === 'years' && Array.isArray(value)) {
        standardParams.years = value.map((v) => parseInt(String(v), 10));
      } else {
        standardParams[key] = value;
      }
    } else {
      // It's a dynamic filter! Move it to 'attributes'
      // Ensure value is an array of strings
      const valuesArray = Array.isArray(value)
        ? value.map(String)
        : [String(value)];
      attributes.push({ key, values: valuesArray });
    }
  });

  return {
    ...standardParams,
    attributes: attributes.length > 0 ? attributes : undefined,
  };
};
